import type {
  CampaignRecord,
  ClientRecord,
  ContentRecord,
  GenerationRecord,
  InvoiceRecord,
  MemoryRecord,
  Profile,
  TaskRecord,
  WorkspaceData,
} from "./types";

export type TestEditorKind = "task" | "content" | "client" | "campaign" | "invoice" | "memory" | "profile";

const STORAGE_KEY = "creator-campaign-desk-test-workspace-v1";
const TEST_USER_ID = "browser-test-user";

function dateOffset(days: number) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function dateTimeOffset(days: number, hour: number) {
  return `${dateOffset(days)}T${String(hour).padStart(2, "0")}:00:00.000Z`;
}

function localId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createTestWorkspace(): WorkspaceData {
  const clientId = "test-client-luma";
  const campaignId = "test-campaign-launch";
  return {
    profile: {
      id: "test-profile",
      display_name: "Avery Creator",
      brand_name: "Avery Makes",
      niche: "Culture, style, and everyday design",
      audience: "Curious 20 to 35 year olds who value practical taste and honest recommendations.",
      voice: "Specific, warm, observant, and lightly witty",
      goals: "Publish consistently, protect creative control, and make brand work commercially sustainable.",
      avoid: "Hype, false urgency, unverified claims, and generic creator language",
      platforms: ["Instagram", "YouTube"],
      onboarding_completed: true,
    },
    subscription: {
      id: "test-subscription",
      user_id: TEST_USER_ID,
      plan: "free",
      status: "free",
      current_period_end: null,
      cancel_at_period_end: false,
      ai_generations_used: 2,
    },
    tasks: [
      { id: "test-task-1", user_id: TEST_USER_ID, title: "Confirm usage-rights end date", context: "Luma launch", due_date: dateOffset(0), completed: false },
      { id: "test-task-2", user_id: TEST_USER_ID, title: "Review three opening hooks", context: "Studio", due_date: dateOffset(0), completed: false },
      { id: "test-task-3", user_id: TEST_USER_ID, title: "Record invoice follow-up", context: "Money", due_date: dateOffset(1), completed: false },
    ],
    clients: [
      { id: clientId, user_id: TEST_USER_ID, name: "Luma Living", contact_name: "Maya Shah", contact_email: "maya@example.test", preferred_channel: "Email", relationship_stage: "active", notes: "Values concise approvals. Never send without creator review." },
      { id: "test-client-north", user_id: TEST_USER_ID, name: "North Objects", contact_name: "Leo Martin", contact_email: "leo@example.test", preferred_channel: "Email", relationship_stage: "prospect", notes: "Interested in a three-video product education series." },
    ],
    campaigns: [
      {
        id: campaignId,
        user_id: TEST_USER_ID,
        client_id: clientId,
        name: "Luma Summer Launch",
        status: "in_production",
        fee_minor: 18500000,
        currency: "INR",
        due_date: dateOffset(4),
        deliverables: "One Reel, three Stories, and one usage-safe still",
        required_claims: "Product materials and care details from the approved brief",
        prohibited_claims: "No sustainability superlatives and no performance guarantees",
        disclosure: "Paid partnership",
        usage_rights: "Organic brand channels for 90 days. Paid use requires a new approval.",
        exclusivity: "Home decor, 14 days from first publication",
        revision_cap: 2,
        payment_terms: "50% on approval, 50% within 15 days of delivery",
        attention: "Confirm the exact organic usage end date before approval.",
        brief_text: "Show how one considered object changes a small daily ritual. Keep the product use natural and specific.",
      },
    ],
    content: [
      { id: "test-content-1", user_id: TEST_USER_ID, campaign_id: campaignId, title: "The five-minute reset", platform: "Instagram", format: "Reel", status: "review", planned_at: dateTimeOffset(1, 18), body: "Open on the untidy table, then reveal the one-object reset. End on the real evening routine.", source: "manual", externally_published: false, external_url: null },
      { id: "test-content-2", user_id: TEST_USER_ID, campaign_id: null, title: "What good design removes", platform: "YouTube", format: "Short", status: "draft", planned_at: dateTimeOffset(3, 12), body: "A 40-second observation about friction, visual noise, and objects that earn their place.", source: "manual", externally_published: false, external_url: null },
      { id: "test-content-3", user_id: TEST_USER_ID, campaign_id: null, title: "Sunday studio notes", platform: "Instagram", format: "Carousel", status: "scheduled", planned_at: dateTimeOffset(5, 10), body: "Five lessons from this week's creator workflow.", source: "manual", externally_published: false, external_url: null },
    ],
    invoices: [
      { id: "test-invoice-1", user_id: TEST_USER_ID, client_id: clientId, campaign_id: campaignId, reference: "INV-1042", amount_minor: 9250000, currency: "INR", due_date: dateOffset(-4), status: "due", received_minor: 0, notes: "Draft a follow-up, then review before sending outside the Desk." },
      { id: "test-invoice-2", user_id: TEST_USER_ID, client_id: "test-client-north", campaign_id: null, reference: "INV-1038", amount_minor: 6000000, currency: "INR", due_date: dateOffset(-12), status: "paid", received_minor: 6000000, notes: "Recorded as paid by the creator." },
    ],
    memory: [
      { id: "test-memory-1", user_id: TEST_USER_ID, kind: "creative_preference", title: "Open with an observed tension", body: "Strong posts begin with a precise moment the audience recognises, not a broad promise.", source: "creator_entered", confirmed: true, excluded: false },
      { id: "test-memory-2", user_id: TEST_USER_ID, kind: "commercial_lesson", title: "Usage needs an end date", body: "Never approve usage language that has a channel but no duration.", source: "confirmed_record", confirmed: true, excluded: false },
    ],
    generations: [
      { id: "test-generation-1", user_id: TEST_USER_ID, tool: "hooks", prompt: "A small evening reset using one well-designed object", output: "The object that made my five-minute reset actually take five minutes.", model: "browser-local-test", campaign_id: campaignId, feedback: null, created_at: new Date().toISOString() },
    ],
    attachments: [],
  };
}

export function loadTestWorkspace(): WorkspaceData {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved) as WorkspaceData;
  } catch {
    // Storage can be unavailable in private browsing. The in-memory workspace still works.
  }
  const workspace = createTestWorkspace();
  saveTestWorkspace(workspace);
  return workspace;
}

export function saveTestWorkspace(workspace: WorkspaceData) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(workspace));
  } catch {
    // Keep the current in-memory session usable if storage is unavailable.
  }
}

export function resetTestWorkspace() {
  const workspace = createTestWorkspace();
  saveTestWorkspace(workspace);
  return workspace;
}

function upsert<T extends { id: string }>(items: T[], record: T) {
  const index = items.findIndex((item) => item.id === record.id);
  if (index < 0) return [record, ...items];
  return items.map((item) => item.id === record.id ? record : item);
}

export function saveTestRecord(workspace: WorkspaceData, kind: TestEditorKind, recordId: string | null, payload: Record<string, unknown>): WorkspaceData {
  if (kind === "profile") {
    return { ...workspace, profile: { ...workspace.profile, ...payload } as Profile };
  }

  const base = { id: recordId || localId(kind), user_id: TEST_USER_ID };
  if (kind === "task") {
    const existing = workspace.tasks.find((item) => item.id === recordId);
    const record = { ...base, title: "", context: "", due_date: null, completed: false, ...existing, ...payload } as TaskRecord;
    return { ...workspace, tasks: upsert(workspace.tasks, record) };
  }
  if (kind === "content") {
    const existing = workspace.content.find((item) => item.id === recordId);
    const record = { ...base, campaign_id: null, title: "Untitled", platform: "Instagram", format: "Post", status: "idea", planned_at: null, body: "", source: "manual", externally_published: false, external_url: null, ...existing, ...payload } as ContentRecord;
    return { ...workspace, content: upsert(workspace.content, record) };
  }
  if (kind === "client") {
    const existing = workspace.clients.find((item) => item.id === recordId);
    const record = { ...base, name: "Untitled client", contact_name: "", contact_email: "", preferred_channel: "", relationship_stage: "prospect", notes: "", ...existing, ...payload } as ClientRecord;
    return { ...workspace, clients: upsert(workspace.clients, record) };
  }
  if (kind === "campaign") {
    const existing = workspace.campaigns.find((item) => item.id === recordId);
    const record = { ...base, client_id: null, name: "Untitled campaign", status: "brief_received", fee_minor: 0, currency: "INR", due_date: null, deliverables: "", required_claims: "", prohibited_claims: "", disclosure: "", usage_rights: "", exclusivity: "", revision_cap: null, payment_terms: "", attention: "", brief_text: "", ...existing, ...payload } as CampaignRecord;
    return { ...workspace, campaigns: upsert(workspace.campaigns, record) };
  }
  if (kind === "invoice") {
    const existing = workspace.invoices.find((item) => item.id === recordId);
    const record = { ...base, client_id: null, campaign_id: null, reference: "INV-", amount_minor: 0, currency: "INR", due_date: null, status: "draft", received_minor: 0, notes: "", ...existing, ...payload } as InvoiceRecord;
    return { ...workspace, invoices: upsert(workspace.invoices, record) };
  }
  const existing = workspace.memory.find((item) => item.id === recordId);
  const record = { ...base, kind: "creative_preference", title: "Untitled memory", body: "", source: "creator_entered", confirmed: true, excluded: false, ...existing, ...payload } as MemoryRecord;
  return { ...workspace, memory: upsert(workspace.memory, record) };
}

export function deleteTestRecord(workspace: WorkspaceData, table: string, id: string): WorkspaceData {
  if (table === "tasks") return { ...workspace, tasks: workspace.tasks.filter((item) => item.id !== id) };
  if (table === "content_items") return { ...workspace, content: workspace.content.filter((item) => item.id !== id) };
  if (table === "clients") return { ...workspace, clients: workspace.clients.filter((item) => item.id !== id) };
  if (table === "campaigns") return { ...workspace, campaigns: workspace.campaigns.filter((item) => item.id !== id) };
  if (table === "invoices") return { ...workspace, invoices: workspace.invoices.filter((item) => item.id !== id) };
  if (table === "memory_notes") return { ...workspace, memory: workspace.memory.filter((item) => item.id !== id) };
  return workspace;
}

export function addTestGeneration(workspace: WorkspaceData, input: { tool: string; prompt: string; campaignId?: string }, output: string): WorkspaceData {
  const generation: GenerationRecord = {
    id: localId("generation"),
    user_id: TEST_USER_ID,
    tool: input.tool,
    prompt: input.prompt,
    output,
    model: "browser-local-test",
    campaign_id: input.campaignId || null,
    feedback: null,
    created_at: new Date().toISOString(),
  };
  return {
    ...workspace,
    subscription: { ...workspace.subscription, ai_generations_used: workspace.subscription.ai_generations_used + 1 },
    generations: [generation, ...workspace.generations],
  };
}

export function makeTestDraft(tool: string, prompt: string, voice: string) {
  const subject = prompt.trim();
  const tone = voice || "clear and creator-led";
  if (tool === "hooks") return `1. I thought ${subject.toLowerCase()} needed more effort. It needed a better first decision.\n\n2. The part of ${subject.toLowerCase()} nobody shows you.\n\n3. Before you add another step, notice what is creating the friction.\n\n4. This is what changed when I stopped making the obvious choice.\n\n5. A practical way to make ${subject.toLowerCase()} feel more intentional.\n\nVoice direction: ${tone}.`;
  if (tool === "captions") return `${subject}\n\nThe useful part was not doing more. It was noticing the one decision that kept creating friction, then changing that first.\n\nSave this for the next time the process feels heavier than it should.\n\nCreator review: replace this final line with a specific audience question before publishing.`;
  if (tool === "script") return `OPEN (0-3s)\nShow the real moment behind: ${subject}.\n\nTENSION (3-12s)\nName the friction in one specific sentence. Avoid a broad promise.\n\nTURN (12-28s)\nDemonstrate the decision or method. Keep the evidence visible.\n\nPAYOFF (28-38s)\nState what changed, without overstating the result.\n\nCLOSE (38-45s)\nInvite the audience to compare it with their own routine.\n\nVoice direction: ${tone}.`;
  if (tool === "brief") return `CREATIVE ROUTE\n${subject}\n\nAudience tension: They want a useful answer without feeling sold to.\nCreator angle: Begin with an observed moment, then let the product or idea earn its role.\nProof to capture: One real action, one close detail, and one honest limitation.\nApproval check: Confirm claims, disclosure, rights duration, and revision scope before delivery.`;
  if (tool === "repurpose") return `SOURCE IDEA\n${subject}\n\n1. Reel: One observed tension, one demonstration, one measured takeaway.\n2. Carousel: Problem, false assumption, working method, example, creator conclusion.\n3. Stories: Context, process clip, audience question.\n4. Short caption: The single most useful line from the source.\n5. Follow-up: Answer the strongest audience objection with evidence.`;
  if (tool === "trend_angle") return `HYPOTHESIS ONLY\nPotential angle: Connect ${subject.toLowerCase()} to a recurring audience behaviour, not a claimed live trend.\n\nTest: Publish two openings with the same core idea. Compare saves, qualified replies, and completion.\n\nDo not label this a trend until a verified external source is connected.`;
  return `FIVE CONTENT ROUTES FOR: ${subject}\n\n1. The mistake I made before I understood the real constraint.\n2. A before-and-after built around one visible decision.\n3. Three details most people skip, and why they matter.\n4. The honest limitation that makes the recommendation more useful.\n5. A working checklist the audience can apply today.\n\nVoice direction: ${tone}.`;
}
